"use client";

import { AlertTriangle, RefreshCw } from "lucide-react";
import { useTranslations } from "next-intl";
import { ReactNode, useCallback, useState } from "react";

import { ErrorBoundary } from "@/shared/lib/error-boundary";
import { Button } from "@/shared/ui/buttons/button";

/**
 * Граница ошибки размером с блок, а не со страницу.
 *
 * На странице турнира два десятка источников данных и несколько независимых
 * блоков: сетка, участники, статистика, стримы, лента. Падение одного из них
 * уносило всю страницу — при том, что остальные блоки в порядке и человеку
 * нужны. Здесь падение остаётся внутри блока: на его месте компактное
 * сообщение с «Повторить», страница живёт.
 *
 * Молчащий фолбэк хуже падения — пустой прямоугольник читается как «данных
 * нет», поэтому сообщение всегда видимое, а ошибка всегда уходит в отчёт
 * с именем блока (KAN-994).
 */
export function WidgetBoundary({
  name,
  children,
  onRetry,
  className,
}: {
  /** Имя блока: попадает в GlitchTip тегом widget и в отчёт о сбое. */
  name: string;
  children: ReactNode;
  /**
   * Что перезапросить при «Повторить» — обычно `refetch` конкретного запроса.
   * Без него блок просто перемонтируется.
   */
  onRetry?: () => void;
  className?: string;
}) {
  // Ключ сбрасывает поддерево: без него после сброса границы отрисовался бы
  // тот же инстанс с тем же состоянием и упал бы снова.
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    onRetry?.();
    setAttempt((n) => n + 1);
  }, [onRetry]);

  return (
    <ErrorBoundary
      key={attempt}
      name={name}
      fallback={<WidgetErrorFallback onRetry={retry} className={className} />}
    >
      {children}
    </ErrorBoundary>
  );
}

function WidgetErrorFallback({
  onRetry,
  className,
}: {
  onRetry: () => void;
  className?: string | undefined;
}) {
  const tUi = useTranslations("Ui");
  return (
    <div
      className={[
        "flex flex-col items-center justify-center gap-2 rounded-lg border border-border bg-muted/30 p-4 text-center",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="alert"
    >
      <AlertTriangle className="h-5 w-5 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">
        {tUi("widgetBoundary.etotBlokNeOtobrazilsya")}
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="mr-2 h-3.5 w-3.5" />
        {tUi("widgetBoundary.povtorit")}
      </Button>
    </div>
  );
}
